import React, { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCircle2, KeyRound, MapPin, LogOut, Loader2 } from "lucide-react";

export default function CustomerProfile() {
    const { user, refresh, logout } = useAuth();
    const [districts, setDistricts] = useState([]);
    const [districtSlug, setDistrictSlug] = useState(user?.district_slug || "");
    const [savingDist, setSavingDist] = useState(false);
    const [pwd, setPwd] = useState({ old: "", next: "" });
    const [savingPwd, setSavingPwd] = useState(false);

    useEffect(() => { api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {}); }, []);
    useEffect(() => { setDistrictSlug(user?.district_slug || ""); }, [user?.district_slug]);

    const saveDistrict = async () => {
        if (!districtSlug) return;
        setSavingDist(true);
        try {
            await api.patch("/auth/customer/complete-profile", { district_slug: districtSlug });
            await refresh();
            toast.success("District updated");
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSavingDist(false); }
    };

    const changePassword = async (e) => {
        e.preventDefault();
        if (pwd.next.length < 8) return toast.error("New password must be at least 8 characters");
        setSavingPwd(true);
        try {
            await api.post("/auth/change-password", { old_password: pwd.old, new_password: pwd.next });
            toast.success("Password updated");
            setPwd({ old: "", next: "" });
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSavingPwd(false); }
    };

    return (
        <div data-testid="customer-profile" className="max-w-2xl">
            <h1 className="section-title text-2xl">Profile</h1>

            <div className="card-soft mt-4 flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-orange-100 text-[var(--brand-orange)]"><UserCircle2 size={26} /></div>
                <div>
                    <div className="font-heading text-base font-semibold text-[var(--text-primary)]">{user?.full_name}</div>
                    <div className="text-xs text-[var(--text-muted)]">{user?.mobile_number || user?.email}</div>
                </div>
            </div>

            <div className="card-soft mt-3">
                <div className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-[var(--text-primary)]"><MapPin size={14} /> Home district</div>
                <div className="flex items-end gap-2">
                    <div className="flex-1">
                        <Label className="text-xs">District</Label>
                        <Select value={districtSlug || undefined} onValueChange={setDistrictSlug}>
                            <SelectTrigger data-testid="prof-district"><SelectValue placeholder="Pick your district" /></SelectTrigger>
                            <SelectContent>
                                {districts.map((d) => (<SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                    <button
                        type="button"
                        onClick={saveDistrict}
                        disabled={savingDist || districtSlug === (user?.district_slug || "")}
                        data-testid="prof-district-save"
                        className="btn-primary disabled:opacity-50"
                    >
                        {savingDist && <Loader2 size={14} className="animate-spin" />}
                        Save
                    </button>
                </div>
            </div>

            {user?.mobile_number && (
                <div className="card-soft mt-3">
                    <div className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-[var(--text-primary)]"><KeyRound size={14} /> Change password</div>
                    <form onSubmit={changePassword} className="grid gap-3">
                        <div>
                            <Label className="text-xs">Current password</Label>
                            <Input type="password" value={pwd.old} onChange={(e) => setPwd((p) => ({ ...p, old: e.target.value }))} required data-testid="prof-old-pw" />
                        </div>
                        <div>
                            <Label className="text-xs">New password</Label>
                            <Input type="password" value={pwd.next} onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))} minLength={8} required data-testid="prof-new-pw" />
                        </div>
                        <button type="submit" disabled={savingPwd} className="btn-primary w-fit disabled:opacity-50" data-testid="prof-pw-submit">
                            {savingPwd && <Loader2 size={14} className="animate-spin" />} Update password
                        </button>
                    </form>
                </div>
            )}

            <button type="button" onClick={logout} className="btn-secondary mt-6" data-testid="prof-logout">
                <LogOut size={14} /> Log out
            </button>
        </div>
    );
}
